import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { Button, Props as ButtonProps } from 'app/components/Common/Button'
import { FadeInOutAnimation } from 'app/components/Common/FadeInOutAnimation'
import { FlexBox } from 'app/components/Layout/FlexBox'
import React, { memo } from 'react'
import LazyLoad from 'react-lazyload'

export interface Props {
  cta?: ButtonProps[]
  description?: string
  image?: string
  title?: string
}

export const Info = memo(function Info({
  cta,
  description,
  image,
  title,
}: Props) {
  return (
    <Container>
      <FadeInOutAnimation>
        <Wrapper row space="between" stretch>
          <Left>
            {image ? (
              <LazyLoad>
                <Image
                  className="info-image"
                  style={{ backgroundImage: `url(${image})` }}
                />
              </LazyLoad>
            ) : null}
          </Left>
          <Center>
            {image ? (
              <LazyLoad>
                <MainImage style={{ backgroundImage: `url(${image})` }} />
              </LazyLoad>
            ) : null}
            <TextWrap>
              {title ? <Title>{title}</Title> : null}
              {description ? (
                <Description
                  dangerouslySetInnerHTML={{ __html: description }}
                />
              ) : null}
              {cta
                ? cta.map((item, index) => <Button key={index} {...item} />)
                : null}
            </TextWrap>
          </Center>
          <Right>
            {image ? (
              <LazyLoad>
                <Image
                  className="info-image"
                  style={{ backgroundImage: `url(${image})` }}
                />
              </LazyLoad>
            ) : null}
          </Right>
        </Wrapper>
      </FadeInOutAnimation>
    </Container>
  )
})

const Container = styled.section`
  margin: 10.5rem auto;

  @media (max-width: 1199px) {
    margin: 7.5rem auto;
  }

  @media (max-width: 767px) {
    margin: 5.25rem auto;
  }
`

const Wrapper = styled(FlexBox)`
  max-width: 73.5rem;
  margin: auto;
  padding: 0 1.5rem;

  @media (max-width: 767px) {
    padding: 0;
  }
`

const Left = styled.div`
  width: 26%;
  position: relative;
  .info-image {
    background-position: left center;
  }

  @media (max-width: 1023px) {
    width: 20%;
  }

  @media (max-width: 767px) {
    display: none;
  }
`

const Center = styled.div`
  width: calc(48% - 4px);
  position: relative;

  @media (max-width: 1023px) {
    width: calc(60% - 4px);
  }

  @media (max-width: 767px) {
    width: 100%;
  }
`

const Right = styled.div`
  width: 26%;
  position: relative;
  .info-image {
    background-position: right center;
  }

  @media (max-width: 1023px) {
    width: 20%;
  }

  @media (max-width: 767px) {
    display: none;
  }
`

const BackgroundStyle = css`
  width: 100%;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  left: 0;
`

const Image = styled.div`
  height: calc(100% - 6rem);
  opacity: 0.2;
  top: 3rem;
  ${BackgroundStyle}
`

const MainImage = styled.div`
  height: 100%;
  top: 0;
  background-position: center;
  ${BackgroundStyle}
`

const TextWrap = styled.div`
  background: ${({ theme }) => theme.colors.variants.neutralLight4};
  margin: 3rem;
  overflow: hidden;
  padding: 4.875rem 1.5rem 0;
  position: relative;
  text-align: center;

  @media (max-width: 1023px) {
    margin: 1.5rem;
    padding: 3.75rem 1.5rem 0;
  }

  @media (max-width: 767px) {
    padding-top: 3rem;
  }
`

const Title = styled.h2`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 1.5rem;
  line-height: 2rem;
  margin-bottom: 2.25rem;
  &:before,
  &:after {
    content: '';
    flex: 1 1;
    background: ${({ theme }) => theme.colors.variants.neutralLight1};
    height: 2px;
  }
  &:before {
    margin-right: 1.5rem;
  }
  &:after {
    margin-left: 1.5rem;
  }
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark3};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 0.9375rem;
  line-height: 2.875rem;
  margin-bottom: 4.875rem;

  @media (max-width: 767px) {
    margin-bottom: 3.75rem;
  }
`
